/**
 * JavaScript accessibile per il menu dell'header
 * Gestisce focus management, navigazione da tastiera e ARIA states
 */

class AccessibleHeaderMenu {
    constructor() {
        this.mobileMenuTrigger = document.querySelector('.header-mobile__trigger[aria-controls="mobile-menu"]');
        this.mobileMenu = document.getElementById('mobile-menu');
        this.closeTrigger = this.mobileMenu?.querySelector('.header-mobile__trigger');
        this.menuItems = this.mobileMenu?.querySelectorAll('[role="menuitem"]');
        this.submenuButtons = this.mobileMenu?.querySelectorAll('button[aria-haspopup="true"]');
        
        // Menu Desktop
        this.desktopMenu = document.querySelector('.header__menu');
        this.desktopMenuItems = this.desktopMenu?.querySelectorAll('li.menu-item-has-children');
        
        this.isMenuOpen = false;
        this.lastFocusedElement = null;
        this.currentOpenSubmenu = null;
        
        this.init();
    }

    init() {
        this.initMobileMenu();
        this.initDesktopMenu();
    }

    initMobileMenu() {
        if (!this.mobileMenuTrigger || !this.mobileMenu) return;
        
        // Event listeners per apertura/chiusura menu
        this.mobileMenuTrigger.addEventListener('click', this.toggleMenu.bind(this));
        this.closeTrigger?.addEventListener('click', this.closeMenu.bind(this));
        
        // Event listener per ESC key
        document.addEventListener('keydown', this.handleEscapeKey.bind(this));
        
        // Event listeners per sottomenu
        this.submenuButtons.forEach(button => {
            button.addEventListener('click', this.toggleSubmenu.bind(this));
            button.addEventListener('keydown', this.handleSubmenuKeydown.bind(this));
        });
        
        // Event listener per navigazione da tastiera nel menu
        this.menuItems?.forEach(item => {
            item.addEventListener('keydown', this.handleMenuKeydown.bind(this));
        });
        
        // Trap focus nel menu quando è aperto
        this.mobileMenu.addEventListener('keydown', this.trapFocus.bind(this));
        
        // Chiudi menu cliccando fuori
        document.addEventListener('click', this.handleOutsideClick.bind(this));
    }

    initDesktopMenu() {
        if (!this.desktopMenu || !this.desktopMenuItems) return;
        
        this.desktopMenuItems.forEach(menuItem => {
            const link = menuItem.querySelector('a');
            const submenu = menuItem.querySelector('.sub-menu');
            
            if (!link || !submenu) return;
            
            // Variabile per tenere traccia del timer
            let closeTimeout;
            
            // Funzione per cancellare il timer di chiusura
            const clearCloseTimer = () => {
                if (closeTimeout) {
                    clearTimeout(closeTimeout);
                    closeTimeout = null;
                }
            };

            // Funzione per gestire l'apertura
            const handleMouseEnter = () => {
                if (window.innerWidth > 1024) {
                    clearCloseTimer();
                    this.openDesktopSubmenu(menuItem);
                }
            };

            // Funzione per gestire la chiusura con delay
            const handleMouseLeave = (event) => {
                if (window.innerWidth > 1024) {
                    clearCloseTimer();
                    closeTimeout = setTimeout(() => {
                        // Verifica se il mouse è sopra il menuItem o il submenu
                        const menuRect = menuItem.getBoundingClientRect();
                        const submenuRect = submenu.getBoundingClientRect();
                        const mouseX = event.clientX;
                        const mouseY = event.clientY;

                        // Crea un'area che include il menuItem, il submenu e lo spazio tra loro
                        const minX = Math.min(menuRect.left, submenuRect.left);
                        const maxX = Math.max(menuRect.right, submenuRect.right);
                        const minY = Math.min(menuRect.top, submenuRect.top);
                        const maxY = Math.max(menuRect.bottom, submenuRect.bottom);

                        // Se il mouse è fuori dall'area totale, chiudi il menu
                        if (mouseX < minX || mouseX > maxX || mouseY < minY || mouseY > maxY) {
                            this.closeDesktopSubmenu(menuItem);
                        }
                    }, 50); // Ridotto il delay a 50ms per una risposta più veloce
                }
            };

            // Aggiungi gli event listener per hover
            menuItem.addEventListener('mouseenter', handleMouseEnter);
            submenu.addEventListener('mouseenter', handleMouseEnter);
            menuItem.addEventListener('mouseleave', handleMouseLeave);
            submenu.addEventListener('mouseleave', handleMouseLeave);
            
            // Aggiungi event listener per il movimento del mouse sul documento
            document.addEventListener('mousemove', (event) => {
                if (window.innerWidth > 1024 && this.currentOpenSubmenu === menuItem) {
                    const menuRect = menuItem.getBoundingClientRect();
                    const submenuRect = submenu.getBoundingClientRect();
                    const mouseX = event.clientX;
                    const mouseY = event.clientY;

                    // Crea un'area che include il menuItem, il submenu e lo spazio tra loro
                    const minX = Math.min(menuRect.left, submenuRect.left);
                    const maxX = Math.max(menuRect.right, submenuRect.right);
                    const minY = Math.min(menuRect.top, submenuRect.top);
                    const maxY = Math.max(menuRect.bottom, submenuRect.bottom);

                    // Se il mouse è fuori dall'area totale, chiudi il menu
                    if (mouseX < minX || mouseX > maxX || mouseY < minY || mouseY > maxY) {
                        this.closeDesktopSubmenu(menuItem);
                    }
                }
            });
            
            // Nascondi il bottone back dalla navigazione da tastiera in desktop
            const backButton = submenu.querySelector('.back-button');
            if (backButton) {
                backButton.setAttribute('tabindex', '-1');
                // Nascondi anche agli screen reader in desktop
                const updateBackButtonVisibility = () => {
                    if (window.innerWidth > 1024) {
                        backButton.setAttribute('aria-hidden', 'true');
                    } else {
                        backButton.removeAttribute('aria-hidden');
                    }
                };
                // Esegui subito e aggiungi listener per il resize
                updateBackButtonVisibility();
                window.addEventListener('resize', updateBackButtonVisibility);
            }
            
            // Aggiungi attributi ARIA
            link.setAttribute('aria-haspopup', 'true');
            link.setAttribute('aria-expanded', 'false');
            submenu.id = `desktop-submenu-${Date.now()}-${Math.random()}`;
            link.setAttribute('aria-controls', submenu.id);
            submenu.setAttribute('role', 'menu');
            submenu.setAttribute('aria-hidden', 'true');
            
            // Event listener per focus
            link.addEventListener('focus', () => {
                if (window.innerWidth > 1024) {
                    this.openDesktopSubmenu(menuItem);
                }
            });
            
            // Event listener per blur
            const handleBlur = () => {
                if (window.innerWidth > 1024) {
                    setTimeout(() => {
                        if (!menuItem.contains(document.activeElement)) {
                            this.closeDesktopSubmenu(menuItem);
                        }
                    }, 10);
                }
            };
            
            // Aggiungi event listener per blur al link principale
            link.addEventListener('blur', handleBlur);
            
            // Event listener per click
            link.addEventListener('click', (e) => {
                if (window.innerWidth > 1024) {
                    e.preventDefault();
                    if (menuItem === this.currentOpenSubmenu) {
                        this.closeDesktopSubmenu(menuItem);
                    } else {
                        this.openDesktopSubmenu(menuItem);
                    }
                }
            });
            
            // Event listener per keydown
            link.addEventListener('keydown', (e) => {
                if (window.innerWidth > 1024) {
                    switch (e.key) {
                        case 'Enter':
                        case ' ':
                        case 'ArrowDown':
                            e.preventDefault();
                            this.openDesktopSubmenu(menuItem);
                            const firstSubmenuLink = submenu.querySelector('a');
                            if (firstSubmenuLink) {
                                firstSubmenuLink.focus();
                            }
                            break;
                        case 'Escape':
                            e.preventDefault();
                            this.closeDesktopSubmenu(menuItem);
                            link.focus();
                            break;
                    }
                }
            });
            
            // Gestisci i link del sottomenu
            const submenuLinks = submenu.querySelectorAll('a');
            submenuLinks.forEach((submenuLink, index) => {
                submenuLink.setAttribute('role', 'menuitem');
                
                // Aggiungi event listener per blur
                submenuLink.addEventListener('blur', handleBlur);
                
                submenuLink.addEventListener('keydown', (e) => {
                    if (window.innerWidth > 1024) {
                        switch (e.key) {
                            case 'ArrowUp':
                                e.preventDefault();
                                if (index === 0) {
                                    link.focus();
                                } else {
                                    submenuLinks[index - 1].focus();
                                }
                                break;
                            case 'ArrowDown':
                                e.preventDefault();
                                if (index < submenuLinks.length - 1) {
                                    submenuLinks[index + 1].focus();
                                }
                                break;
                            case 'Escape':
                                e.preventDefault();
                                this.closeDesktopSubmenu(menuItem);
                                link.focus();
                                break;
                            case 'Tab':
                                // Non prevenire il comportamento predefinito del tab
                                // Il blur handler si occuperà di chiudere il menu se necessario
                                break;
                        }
                    }
                });
            });
        });
        
        // Chiudi sottomenu quando si clicca fuori
        document.addEventListener('click', (e) => {
            if (!this.desktopMenu.contains(e.target)) {
                this.closeAllDesktopSubmenus();
            }
        });
    }

    openDesktopSubmenu(menuItem) {
        // Chiudi altri sottomenu aperti
        this.closeAllDesktopSubmenus();
        
        const link = menuItem.querySelector('a');
        const submenu = menuItem.querySelector('.sub-menu');
        
        if (!submenu) return;
        
        // Aggiorna stati ARIA
        link.setAttribute('aria-expanded', 'true');
        submenu.setAttribute('aria-hidden', 'false');
        
        // Aggiungi classe per styling
        menuItem.classList.add('submenu-open');
        
        this.currentOpenSubmenu = menuItem;
    }

    closeDesktopSubmenu(menuItem) {
        const link = menuItem.querySelector('a');
        const submenu = menuItem.querySelector('.sub-menu');
        
        if (!submenu) return;
        
        // Aggiorna stati ARIA
        link.setAttribute('aria-expanded', 'false');
        submenu.setAttribute('aria-hidden', 'true');
        
        // Rimuovi classe per styling
        menuItem.classList.remove('submenu-open');
        
        if (this.currentOpenSubmenu === menuItem) {
            this.currentOpenSubmenu = null;
        }
    }

    closeAllDesktopSubmenus() {
        this.desktopMenuItems.forEach(menuItem => {
            this.closeDesktopSubmenu(menuItem);
        });
    }

    handleEscapeKey(event) {
        if (event.key === 'Escape') {
            if (this.isMenuOpen) {
                this.closeMenu();
            } else if (this.currentOpenSubmenu) {
                this.closeAllDesktopSubmenus();
            }
        }
    }

    handleSubmenuKeydown(event) {
        const button = event.target;
        
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                if (button.getAttribute('aria-expanded') === 'false') {
                    this.toggleSubmenu(event);
                } else {
                    this.focusNextMenuItem(button);
                }
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.focusPreviousMenuItem(button);
                break;
            case 'ArrowRight':
                event.preventDefault();
                if (button.getAttribute('aria-expanded') === 'false') {
                    this.toggleSubmenu(event);
                }
                break;
            case 'ArrowLeft':
                event.preventDefault();
                if (button.getAttribute('aria-expanded') === 'true') {
                    this.toggleSubmenu(event);
                }
                break;
        }
    }

    handleMenuKeydown(event) {
        const item = event.target;
        
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.focusNextMenuItem(item);
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.focusPreviousMenuItem(item);
                break;
            case 'Home':
                event.preventDefault();
                this.focusFirstMenuItem();
                break;
            case 'End':
                event.preventDefault();
                this.focusLastMenuItem();
                break;
        }
    }

    focusNextMenuItem(currentItem) {
        const items = Array.from(this.menuItems);
        const currentIndex = items.indexOf(currentItem);
        const nextIndex = (currentIndex + 1) % items.length;
        items[nextIndex].focus();
    }

    focusPreviousMenuItem(currentItem) {
        const items = Array.from(this.menuItems);
        const currentIndex = items.indexOf(currentItem);
        const previousIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1;
        items[previousIndex].focus();
    }

    focusFirstMenuItem() {
        if (this.menuItems && this.menuItems.length > 0) {
            this.menuItems[0].focus();
        }
    }

    focusLastMenuItem() {
        if (this.menuItems && this.menuItems.length > 0) {
            this.menuItems[this.menuItems.length - 1].focus();
        }
    }

    setMenuItemsTabindex(value) {
        this.menuItems?.forEach(item => {
            item.setAttribute('tabindex', value);
        });
    }

    trapFocus(event) {
        if (!this.isMenuOpen) return;
        
        const focusableElements = this.mobileMenu.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        
        if (event.key === 'Tab') {
            if (event.shiftKey) {
                // Shift + Tab
                if (document.activeElement === firstElement) {
                    event.preventDefault();
                    lastElement.focus();
                }
            } else {
                // Tab
                if (document.activeElement === lastElement) {
                    event.preventDefault();
                    firstElement.focus();
                }
            }
        }
    }

    handleOutsideClick(event) {
        if (this.isMenuOpen && 
            !this.mobileMenu.contains(event.target) && 
            !this.mobileMenuTrigger.contains(event.target)) {
            this.closeMenu();
        }
    }

    toggleMenu() {
        if (this.isMenuOpen) {
            this.closeMenu();
        } else {
            this.openMenu();
        }
    }

    openMenu() {
        this.isMenuOpen = true;
        this.lastFocusedElement = document.activeElement;
        
        // Aggiorna ARIA states
        this.mobileMenuTrigger.setAttribute('aria-expanded', 'true');
        this.mobileMenuTrigger.setAttribute('aria-label', 'Chiudi menu di navigazione');
        this.mobileMenu.setAttribute('aria-hidden', 'false');
        this.mobileMenu.removeAttribute('tabindex');
        
        // Aggiorna classi CSS
        this.mobileMenu.classList.add('menu-tendina--active');
        document.querySelector('.hamburger').classList.add('is-active');
        
        // Gestisci focus
        this.setMenuItemsTabindex(0);
        this.focusFirstMenuItem();
        
        // Previeni scroll del body
        document.body.style.overflow = 'hidden';
    }

    closeMenu() {
        this.isMenuOpen = false;
        
        // Aggiorna ARIA states
        this.mobileMenuTrigger.setAttribute('aria-expanded', 'false');
        this.mobileMenuTrigger.setAttribute('aria-label', 'Apri menu di navigazione');
        this.mobileMenu.setAttribute('aria-hidden', 'true');
        this.mobileMenu.setAttribute('tabindex', '-1');
        
        // Aggiorna classi CSS
        this.mobileMenu.classList.remove('menu-tendina--active');
        document.querySelector('.hamburger').classList.remove('is-active');
        
        // Gestisci focus
        this.setMenuItemsTabindex(-1);
        this.closeAllSubmenus();
        
        // Ripristina focus
        if (this.lastFocusedElement) {
            this.lastFocusedElement.focus();
        }
        
        // Ripristina scroll del body
        document.body.style.overflow = '';
    }

    toggleSubmenu(event) {
        const button = event.target.closest('button[aria-haspopup="true"]');
        const submenuId = button.getAttribute('aria-controls');
        const submenu = document.getElementById(submenuId);
        const isExpanded = button.getAttribute('aria-expanded') === 'true';
        
        // Chiudi altri sottomenu allo stesso livello
        this.closeSiblingSubmenus(button);
        
        // Toggle del sottomenu corrente
        button.setAttribute('aria-expanded', !isExpanded);
        submenu.setAttribute('aria-hidden', isExpanded);
        
        if (!isExpanded) {
            // Apri sottomenu
            submenu.style.display = 'block';
            const firstMenuItem = submenu.querySelector('[role="menuitem"]');
            if (firstMenuItem) {
                firstMenuItem.focus();
            }
        } else {
            // Chiudi sottomenu
            submenu.style.display = 'none';
            button.focus();
        }
    }

    closeSiblingSubmenus(currentButton) {
        const parentList = currentButton.closest('ul');
        const siblingButtons = parentList.querySelectorAll('button[aria-haspopup="true"]');
        
        siblingButtons.forEach(button => {
            if (button !== currentButton) {
                const submenuId = button.getAttribute('aria-controls');
                const submenu = document.getElementById(submenuId);
                
                button.setAttribute('aria-expanded', 'false');
                submenu.setAttribute('aria-hidden', 'true');
                submenu.style.display = 'none';
            }
        });
    }

    closeAllSubmenus() {
        this.submenuButtons.forEach(button => {
            const submenuId = button.getAttribute('aria-controls');
            const submenu = document.getElementById(submenuId);
            
            button.setAttribute('aria-expanded', 'false');
            submenu.setAttribute('aria-hidden', 'true');
            submenu.style.display = 'none';
        });
    }
}

// Inizializza il menu accessibile quando il DOM è pronto
document.addEventListener('DOMContentLoaded', () => {
    new AccessibleHeaderMenu();
});

// Aggiorna alla lettura schermo per gli stati del menu
document.addEventListener('DOMContentLoaded', () => {
    const skipLink = document.querySelector('.skip-link');
    if (skipLink) {
        skipLink.addEventListener('click', (event) => {
            event.preventDefault();
            const targetId = skipLink.getAttribute('href').substring(1);
            const target = document.getElementById(targetId);
            if (target) {
                target.focus();
                target.scrollIntoView();
            }
        });
    }
}); 